/**
 * A thin, diegetic sound layer — synthesized at runtime with the Web Audio API.
 *
 * Why synthesized: there are then NO audio asset files to ship or commit, and
 * every sound can be tuned to one calm, coherent palette. This is UI sound ONLY
 * (a page-settle, a warm chime, the cover's landing note); it never generates or
 * touches story CONTENT — the no-runtime-generation invariant is about
 * narration / text / art, not a soft tick at bedtime.
 *
 * Discipline (calm over stimulation): everything here is quiet, short, and
 * rounded — low-passed, softly enveloped, with generous SILENCE between. There
 * is deliberately no sustained drone/room-tone: on a phone speaker at 7:45pm,
 * silence is the more premium bed. Sound stays muted until unlock() runs inside
 * a user gesture (iOS Safari), and respects the same mute as the narration.
 */

type AudioCtor = typeof AudioContext;

function audioCtor(): AudioCtor | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext
  );
}

interface ToneOpts {
  /** Seconds to wait before the note starts. */
  delay?: number;
  /** Total seconds from attack to silence. */
  dur?: number;
  /** Peak gain (0..1); the master keeps everything quiet regardless. */
  gain?: number;
  type?: OscillatorType;
  /** Stereo placement, -1..1 (ignored where unsupported). */
  pan?: number;
  /** Low-pass cutoff (Hz) — rounds off the edge so nothing is harsh. */
  cutoff?: number;
}

class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private noiseBuf: AudioBuffer | null = null;
  private voiceEl: HTMLMediaElement | null = null;
  private voiceAnalyser: AnalyserNode | null = null;

  /** Create/resume the context inside a user gesture. Idempotent and safe. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = audioCtor();
      if (!Ctor) return;
      try {
        this.ctx = new Ctor();
      } catch {
        this.ctx = null;
        return;
      }
      const master = this.ctx.createGain();
      master.gain.value = this.muted ? 0 : 1;
      master.connect(this.ctx.destination);
      this.master = master;
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
  }

  /** Track the narration's mute so muting silences the whole room, not just words. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.ctx) {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setTargetAtTime(muted ? 0 : 1, t, 0.05);
    }
  }

  private live(): { ctx: AudioContext; master: GainNode } | null {
    if (!this.ctx || !this.master || this.muted) return null;
    // Allow "suspended" too: a context created inside a gesture may still be
    // resuming when the first note (the cover note) fires — the note is then
    // scheduled and plays as the context comes up. Only a closed context is out.
    if (this.ctx.state === "closed") return null;
    return { ctx: this.ctx, master: this.master };
  }

  /** One soft, enveloped tone: quick attack, long gentle exponential release. */
  private tone(freq: number, opts: ToneOpts = {}): void {
    const live = this.live();
    if (!live) return;
    const { ctx, master } = live;
    const {
      delay = 0,
      dur = 1.2,
      gain = 0.16,
      type = "sine",
      pan = 0,
      cutoff = 1500,
    } = opts;
    const t0 = ctx.currentTime + delay;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = cutoff;

    const g = ctx.createGain();
    const attack = Math.min(0.1, dur * 0.25);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    osc.connect(lp);
    lp.connect(g);
    let out: AudioNode = g;
    if (pan !== 0 && typeof ctx.createStereoPanner === "function") {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      out = p;
    }
    out.connect(master);

    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  }

  /** The soft single note that lands as you cross into the story. */
  coverNote(): void {
    this.tone(330, { dur: 2.6, gain: 0.15, cutoff: 1200 });
    this.tone(495, { delay: 0.06, dur: 2.3, gain: 0.06, cutoff: 1600 });
  }

  /** A warm two-note chime — the reward when an answer lands. */
  chime(): void {
    this.tone(528, { dur: 1.5, gain: 0.15, pan: -0.12, cutoff: 1900 });
    this.tone(792, { delay: 0.12, dur: 1.7, gain: 0.12, pan: 0.12, cutoff: 2100 });
  }

  /**
   * Tap an analyser onto the narration element so the page can breathe with
   * the narrator's actual voice (the reading lamp swells with the loudness).
   *
   * CONSTRAINTS — read before touching:
   *  - createMediaElementSource permanently reroutes the element through this
   *    context, and a CROSS-ORIGIN src would then output silence. The caller
   *    (App) must only attach when EVERY scene's audio is same-origin.
   *  - One attach per app lifetime, on the single shared narration element
   *    (attaching twice throws). Idempotent for the same element.
   *  - Routed analyser → destination directly (NOT via the UI-sound master),
   *    so the UI mute never double-applies to narration; the element's own
   *    `muted` flag keeps working upstream of the graph.
   */
  attachVoice(el: HTMLAudioElement): AnalyserNode | null {
    if (!this.ctx) return null;
    if (this.voiceEl === el && this.voiceAnalyser) return this.voiceAnalyser;
    if (this.voiceEl && this.voiceEl !== el) return null;
    try {
      const src = this.ctx.createMediaElementSource(el);
      const an = this.ctx.createAnalyser();
      an.fftSize = 1024;
      an.smoothingTimeConstant = 0.55;
      src.connect(an);
      an.connect(this.ctx.destination);
      this.voiceEl = el;
      this.voiceAnalyser = an;
      return an;
    } catch {
      return null;
    }
  }

  /** A soft confirming note when a choice blooms. */
  choiceConfirm(): void {
    this.tone(440, { dur: 1.2, gain: 0.14, cutoff: 1500 });
    this.tone(660, { delay: 0.05, dur: 1.0, gain: 0.05, cutoff: 1800 });
  }

  /** An airy ascending shimmer for the once-a-night wish. */
  wish(): void {
    this.tone(880, { dur: 1.8, gain: 0.09, cutoff: 2400, pan: -0.2 });
    this.tone(1175, { delay: 0.16, dur: 2.2, gain: 0.06, cutoff: 2600, pan: 0.15 });
    this.tone(587, { delay: 0.38, dur: 2.8, gain: 0.05, cutoff: 1800 });
  }

  /** A whisper-soft paper settle as a page turns. Filtered noise, near silent. */
  pageSettle(): void {
    const live = this.live();
    if (!live) return;
    const { ctx, master } = live;
    if (!this.noiseBuf) {
      const len = Math.floor(ctx.sampleRate * 0.25);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuf = buf;
    }
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1050;
    bp.Q.value = 0.7;
    const g = ctx.createGain();
    const t0 = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(0.05, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.19);
    src.connect(bp);
    bp.connect(g);
    g.connect(master);
    src.start(t0);
    src.stop(t0 + 0.22);
  }
}

/** The single shared instance — like the one shared HTMLAudioElement. */
export const sound = new SoundEngine();
